/**
 * The card chrome every Search Detail widget sits in — title, optional subtitle
 * or info tooltip, bordered body.
 *
 * Lifted out of `SearchDetailPage.tsx` when the Search Progress tab arrived and
 * needed the same frame. Two copies of a panel is two places for the border, the
 * heading level and the padding to drift, on two tabs a reader switches between
 * in one click.
 *
 * SUBTITLE *OR* TOOLTIP, and which one is a property of the SURFACE rather than
 * of the panel. Jared, 2026-08-25: *"You can get rid of all of these subheaders…
 * this should be the header and this is the tool tip."* The live tab moved its
 * explanatory prose behind an info icon to buy back the vertical space he was
 * complaining about. The downloaded report keeps it as prose — a document is
 * read on paper as often as on a screen, and a sentence that only exists on
 * hover does not survive being printed. Same component, one prop apart.
 *
 * Not a `@nu-assist/ui` primitive: it is this feature's layout, not a
 * domain-agnostic one, and `ui` already owns a `Panel` with a different job.
 */

import { Tooltip } from "@nu-assist/ui";
import { Info } from "lucide-react";

export interface ReportPanelProps {
  title: string;
  /** Explanatory prose rendered UNDER the title. Mutually exclusive with `tooltip` in practice. */
  subtitle?: string;
  /**
   * The same explanatory prose, behind an info icon beside the title.
   *
   * Hover AND keyboard focus reveal it (`@nu-assist/ui`'s `Tooltip` is
   * CSS-only, no portal), so it is reachable by Tab rather than pointer-only.
   */
  tooltip?: string;
  /** Prefix for the info button's test hook, so two panels on one tab are distinguishable. */
  testId?: string;
  children: React.ReactNode;
}

export function ReportPanel({ title, subtitle, tooltip, testId, children }: ReportPanelProps) {
  return (
    <section className="rounded-md border border-border bg-surface">
      <header className="border-b border-border px-4 py-3">
        <div className="flex items-center gap-1.5">
          <h2 className="text-h2 text-text">{title}</h2>
          {tooltip ? (
            // `maxWidthClassName` widened from the primitive's 220px default,
            // which is sized for the extension's 400px docked panel. These
            // sentences are a full line of prose and a 220px bubble would stack
            // them eight words tall beside a chart that has the room.
            <Tooltip content={tooltip} align="start" side="bottom" maxWidthClassName="max-w-xs">
              {/* A REAL BUTTON inside the trigger, matching `InfoHint`'s
                  precedent: `Tooltip`'s own span reveals on hover and focus but
                  carries no accessible NAME, so the icon alone would give a
                  screen reader a tab stop that announces nothing. The icon is
                  decorative; the label is the name. */}
              <button
                type="button"
                aria-label={`About ${title}`}
                data-testid={`${testId ?? "report-panel"}-info`}
                className="inline-flex items-center justify-center rounded-xs text-text-secondary hover:text-accent focus:outline-none focus:ring-1 focus:ring-accent"
              >
                <Info className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </Tooltip>
          ) : null}
        </div>
        {subtitle ? <p className="mt-0.5 text-caption text-text-secondary">{subtitle}</p> : null}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}
